import { Heading, Paragraph } from '@zillow/constellation';
import type { EvictionRecord } from '@zg-rentals/ts-capps-client';
import {
  getCurrencyDisplay,
  getFormattedAddress,
  getFullNameForBackgroundReport,
} from '@zg-rentals/applications-utils';
import { formatDate } from '@zg-rentals/formatters';
import { capitalize } from 'lodash-es';
import { getTitleCase } from '../../../utils/formatters';

export function InlineDt({ children }: { children: React.ReactNode }) {
  return <dt style={{ display: 'inline' }}>{children}</dt>;
}

export function InlineDd({ children }: { children: React.ReactNode }) {
  return <dd style={{ display: 'inline', marginLeft: 'unset' }}>{children}</dd>;
}

export function EvictionSectionContent({ record }: { record: EvictionRecord }) {
  const {
    firstName,
    middleName,
    lastName,
    address,
    city,
    state,
    zipCode,
    caseFilingDate,
    caseNumber,
    noticeType,
    judgement,
    judgementDate,
    judgementAmount,
    judgementReleaseDate,
    restitutionOfPremises,
    plaintiff,
    plaintiffAttorney,
  } = record;
  const fullName = getFullNameForBackgroundReport({ firstName, middleName, lastName });
  const fullAddress = getFormattedAddress({ street: address, city, state, zip: zipCode });

  function getDispositionOfJudgment(code: string) {
    switch (code) {
      case 'RP':
        return 'Judgment with restitution of premise';
      case 'STIP':
        return 'Stipulated judgment';
      default:
        return getTitleCase(code);
    }
  }

  return (
    <>
      {fullName && <Paragraph fontType="bodySmall">Full name: {fullName}</Paragraph>}
      {fullAddress && <Paragraph fontType="bodySmall">Address: {fullAddress}</Paragraph>}

      {(caseFilingDate || caseNumber || noticeType) && (
        <>
          <Heading level={5} fontColor="coolGray500" fontType="finePrintHeading" marginTop="sm" marginBottom="xs">
            Case details
          </Heading>
          <dl>
            {caseFilingDate && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Case filing date:</InlineDt> <InlineDd>{formatDate(caseFilingDate, 'numbers')}</InlineDd>
              </Paragraph>
            )}
            {caseNumber && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Case number:</InlineDt> <InlineDd>{caseNumber}</InlineDd>
              </Paragraph>
            )}
            {noticeType && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Notice type:</InlineDt> <InlineDd>{capitalize(noticeType)}</InlineDd>
              </Paragraph>
            )}
          </dl>
        </>
      )}

      <Heading level={5} fontColor="coolGray500" fontType="finePrintHeading" marginTop="sm" marginBottom="xs">
        Judgment details
      </Heading>
      <dl>
        {judgement && (
          <Paragraph fontType="bodySmall">
            <InlineDt>Judgment:</InlineDt> <InlineDd>{capitalize(judgement)}</InlineDd>
          </Paragraph>
        )}
        {judgementDate && (
          <Paragraph fontType="bodySmall">
            <InlineDt>Judgment date:</InlineDt> <InlineDd>{formatDate(judgementDate, 'numbers')}</InlineDd>
          </Paragraph>
        )}
        <Paragraph fontType="bodySmall">
          <InlineDt>Judgment amount:</InlineDt> <InlineDd>{getCurrencyDisplay(judgementAmount) || 'N/A'}</InlineDd>
        </Paragraph>
        {judgementReleaseDate && (
          <Paragraph fontType="bodySmall">
            <InlineDt>Judgment release date:</InlineDt>{' '}
            <InlineDd>{formatDate(judgementReleaseDate, 'numbers')}</InlineDd>
          </Paragraph>
        )}
        {restitutionOfPremises && (
          <Paragraph fontType="bodySmall">
            <InlineDt>Disposition of judgment:</InlineDt>{' '}
            <InlineDd>{getDispositionOfJudgment(restitutionOfPremises)}</InlineDd>
          </Paragraph>
        )}
      </dl>

      {(plaintiff || plaintiffAttorney) && (
        <>
          <Heading level={5} fontColor="coolGray500" fontType="finePrintHeading" marginTop="sm" marginBottom="xs">
            Plaintiff details
          </Heading>
          {plaintiff && (
            <Paragraph fontType="bodySmall">
              <InlineDt>Plaintiff’s name:</InlineDt> <InlineDd>{getTitleCase(plaintiff)}</InlineDd>
            </Paragraph>
          )}
          {plaintiffAttorney && (
            <Paragraph fontType="bodySmall">
              <InlineDt>Plaintiff attorney:</InlineDt> <InlineDd>{getTitleCase(plaintiffAttorney)}</InlineDd>
            </Paragraph>
          )}
        </>
      )}
    </>
  );
}
